import { platformDatePickerDisplayFormat } from '@/theme/datePicker';
import { TaskStatusCategory } from '@/types';
import dayjs from '@/utilities/dayjs';

import type { LifecyclePreview, LifecycleProgressSummary, LifecycleStagePreview, LifecycleTaskPreview } from './types';

export const isLifecycleTaskCompleted = (task: LifecycleTaskPreview) =>
    task.completed || task.status?.category === TaskStatusCategory.COMPLETED;

export const lifecycleProgressSummary = (
    lifecycleOrStage: Pick<LifecyclePreview, 'stages'> | Pick<LifecycleStagePreview, 'tasks'>,
): LifecycleProgressSummary => {
    const tasks =
        'stages' in lifecycleOrStage ? lifecycleOrStage.stages.flatMap((stage) => stage.tasks) : lifecycleOrStage.tasks;
    const completedTasks = tasks.filter(isLifecycleTaskCompleted).length;
    const totalTasks = tasks.length;
    const percent = totalTasks === 0 ? 0 : Math.round((completedTasks / totalTasks) * 100);

    return { completedTasks, percent, totalTasks };
};

export const lifecycleProgressLabel = ({ completedTasks, percent, totalTasks }: LifecycleProgressSummary) =>
    `${totalTasks} ${totalTasks === 1 ? 'Task' : 'Tasks'} · ${completedTasks} Completed (${percent}%)`;

export type LifecycleDateTone = 'danger' | 'default' | 'warning';

export interface LifecycleDateDisplay {
    exactDateTime: string;
    label: string;
    tone: LifecycleDateTone;
}

const terminalLifecycleStatuses = new Set(['cancelled', 'canceled', 'complete', 'completed']);

export const lifecycleStatusName = (lifecycle: Pick<LifecyclePreview, 'stateLabel' | 'status'>) =>
    lifecycle.status?.name ?? lifecycle.stateLabel ?? 'Draft';

export const isTerminalLifecycleStatus = (statusName: string) =>
    terminalLifecycleStatuses.has(statusName.trim().toLocaleLowerCase());

export const lifecycleDateDisplay = (
    value: Date | string | null | undefined,
    boundary: 'end' | 'start',
    statusName: string,
    referenceDate = new Date(),
): LifecycleDateDisplay | undefined => {
    const date = dayjs(value);

    if (!value || !date.isValid()) {
        return undefined;
    }

    const now = dayjs(referenceDate);
    const past = date.isBefore(now);
    const prefix = boundary === 'start' ? (past ? 'Started' : 'Starts') : past ? 'Ended' : 'Ends';
    const hoursUntil = date.diff(now, 'hour', true);
    const tone: LifecycleDateTone =
        boundary === 'end' && past && !isTerminalLifecycleStatus(statusName)
            ? 'danger'
            : boundary === 'end' && hoursUntil >= 0 && hoursUntil <= 24
              ? 'warning'
              : 'default';

    return {
        exactDateTime: date.format('MMM. Do, YYYY [at] h:mm A'),
        label: `${prefix} ${date.format(platformDatePickerDisplayFormat)}`,
        tone,
    };
};

export const lifecycleRemainingLabel = (
    value: Date | string | null | undefined,
    statusName: string,
    referenceDate = new Date(),
) => {
    const date = dayjs(value);

    if (!value || !date.isValid()) {
        return undefined;
    }

    const millisecondsRemaining = date.valueOf() - dayjs(referenceDate).valueOf();

    if (millisecondsRemaining < 0) {
        if (isTerminalLifecycleStatus(statusName)) {
            return 'Ended';
        }

        const daysOverdue = Math.max(1, Math.ceil(Math.abs(millisecondsRemaining) / 86_400_000));

        return `${daysOverdue} ${daysOverdue === 1 ? 'day' : 'days'} overdue`;
    }

    const daysRemaining = Math.ceil(millisecondsRemaining / 86_400_000);

    return `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} left`;
};

export const lifecycleTasks = (lifecycle: Pick<LifecyclePreview, 'stages'>) =>
    lifecycle.stages.flatMap((stage) => stage.tasks);
